// pages/app/Docs.tsx — upload/paste a document you authored (spec §6.1).
// Extracts HOW you work, never the subject. Small text is pasted/decoded inline; large
// files and .zip archives (up to 50 MB) upload straight to storage and are processed there.
import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { useServices } from '../../app/AppContext';
import { htmlToText, isHtmlFile, readFileAsText, utf8ToBase64 } from '../../lib/files';
import { SourceList } from '../../components/SourceList';
import { DocsIcon } from '../../components/icons';

const LARGE_BYTES = 1_000_000;
const MAX_UPLOAD_BYTES = 52_428_800; // 50 MB — the Supabase free-tier storage cap.

function isDirectUpload(file: File): boolean {
  return /\.zip$/i.test(file.name) || file.type === 'application/zip' || file.size > LARGE_BYTES;
}

function formatBytes(n: number): string {
  const mb = n / 1_048_576;
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;
}

export function Docs() {
  const { api } = useServices();
  const [fileName, setFileName] = useState('document.md');
  const [text, setText] = useState('');
  const [bigFile, setBigFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-grow the composer: start short, grow with content up to the CSS max-height, then scroll.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [text]);

  async function onFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setError(null);
    setMessage(null);
    // Large files / archives skip the preview and upload straight to storage.
    if (isDirectUpload(file)) {
      setBigFile(file);
      setText('');
      return;
    }
    setBigFile(null);
    try {
      const raw = await readFileAsText(file);
      setText(isHtmlFile(file.type, file.name) ? htmlToText(raw) : raw);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to read file');
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy || (!bigFile && !text.trim())) return;
    if (bigFile && bigFile.size > MAX_UPLOAD_BYTES) {
      setMessage(null);
      setError(`That file is ${formatBytes(bigFile.size)} — the maximum is 50 MB.`);
      return;
    }
    setBusy(true);
    setMessage(null);
    setError(null);
    try {
      if (bigFile) {
        const mimeType = bigFile.type || 'application/octet-stream';
        const { uploadUrl, path } = await api.createUploadUrl({ fileName: bigFile.name });
        await api.putToSignedUrl(uploadUrl, bigFile, mimeType);
        await api.processDocUpload({ path, fileName: bigFile.name, mimeType });
        setBigFile(null);
      } else {
        await api.ingestDoc({
          fileName,
          mimeType: 'text/plain',
          contentBase64: utf8ToBase64(text),
          text,
        });
        setText('');
      }
      setMessage('Uploaded — extraction queued.');
      setReloadKey((k) => k + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-5xl animate-fade-in space-y-6">
      <p className="text-sm text-fg-muted">
        Upload a document you authored. UPLOAD extracts <em>how</em> you work — your patterns and
        reasoning — never the subject matter. Large files and <code>.zip</code> archives (up to
        50&nbsp;MB) are supported.
      </p>
      <form className="card space-y-5" onSubmit={submit}>
        <label className="block">
          <span className="field-label">Load a file</span>
          <div className="flex items-center gap-3 rounded-xl border border-dashed border-line/25 bg-surface-2 px-4 py-4">
            <DocsIcon className="h-5 w-5 shrink-0 text-fg-subtle" />
            <input
              type="file"
              accept=".md,.markdown,.txt,.json,.html,.htm,.zip,text/*,application/zip"
              className="block w-full text-sm text-fg-muted file:mr-3 file:rounded-lg file:border-0 file:bg-surface-3 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-fg hover:file:brightness-95"
              onChange={onFile}
              aria-label="Document file"
            />
          </div>
        </label>

        <label className="block">
          <span className="field-label">File name</span>
          <input
            className="input"
            value={fileName}
            onChange={(e) => setFileName(e.target.value)}
            aria-label="File name"
          />
        </label>

        {bigFile ? (
          <div
            className="rounded-xl border border-line/15 bg-surface-2 px-4 py-3 text-sm"
            data-testid="big-file"
          >
            <p className="font-medium text-fg">{bigFile.name}</p>
            <p className="mt-0.5 text-fg-muted">
              {formatBytes(bigFile.size)} · content hidden — uploaded directly to storage and
              extracted server-side.
            </p>
            <button
              type="button"
              className="btn-ghost mt-2 text-xs"
              onClick={() => setBigFile(null)}
            >
              Remove
            </button>
          </div>
        ) : (
          <label className="block">
            <span className="field-label">Document text</span>
            <textarea
              ref={textareaRef}
              className="textarea max-h-[70vh] min-h-48"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Paste the document you wrote…"
              aria-label="Document text"
            />
          </label>
        )}

        {message ? (
          <p
            className="rounded-xl border border-success/20 bg-success/10 px-3.5 py-2.5 text-sm text-success"
            role="status"
          >
            {message}
          </p>
        ) : null}
        {error ? (
          <p
            className="rounded-xl border border-danger/20 bg-danger/10 px-3.5 py-2.5 text-sm text-danger"
            role="alert"
          >
            {error}
          </p>
        ) : null}

        <button type="submit" className="btn" disabled={busy || (!bigFile && !text.trim())}>
          {busy ? 'Uploading…' : 'Upload'}
        </button>
      </form>

      <SourceList
        kind="docs"
        title="Your uploaded documents"
        emptyText="No documents yet — upload one above to start."
        reloadKey={reloadKey}
        confirmMessage={(s) =>
          `Delete “${s.label ?? 'this document'}”? Everything distilled from it will be removed.`
        }
      />
    </div>
  );
}
